import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { capitalizeEachWords } from "../../../../utils/helpers";

const size = {
  width: 100,
  height: 100,
};

//const COLORS = ["#FF9800", "#8E24AA", "#F06292", "#F50057", "#3384E8"];

// Color mapping for each step
const COLORS: Record<string, string> = {
  Properties: "#FF9800",
  Unifier: "#8E24AA",
  Fetcher: "#F06292",
  Validations: "#F50057",
  Advisor: "#3384E8",
};

// Styled Center Text
const CenterNumber = styled("text")(() => ({
  fill: "#000",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 22,
  fontWeight: 700,
}));

const CenterLabel = styled("text")(() => ({
  fill: "grey",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 10,
  fontWeight: 500,
}));

// Center Label Component
function PieCenterLabel({ total }: { total: number }) {
  const { width, height, left, top } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;

  return (
    <>
      <CenterNumber x={centerX} y={centerY - 10}>
        {total}
      </CenterNumber>
      <CenterLabel width={30} x={centerX} y={centerY + 10}>
        <tspan x={centerX} dy="0">
          Not Ready
        </tspan>
        <tspan x={centerX} dy="10">
          Integration
        </tspan>
      </CenterLabel>
    </>
  );
}

// Custom Legend
function CustomLegend({ data }: { data: any[] }) {
  return (
    <Box display="flex" flexWrap="wrap" width={270} gap="12px" ml={2}>
      {data.map((entry) => (
        <Box key={entry.label}>
          <Typography
            fontWeight={600}
            sx={{
              borderBottom: `2px solid ${entry.color}`,
              fontSize: "13px",
              color: "#00000099",
            }}
          >
            {entry.label}
          </Typography>
          <Box display="flex" gap="8px">
            <Typography sx={{ color: entry.color, fontWeight: 600 }}>
              {entry.count}
            </Typography>
            <Typography sx={{ color: entry.color }}>{entry.percent}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// Main Demo Component
export default function BrandConfigurationReportChart() {
  const { brandConfigurationPIEChartsData } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );

  // const loadedCount = 50;
  // const total = 100;
  // const loadedPerc = (loadedCount / total) * 100;

  // Create chartData from API
  const chartData =
    brandConfigurationPIEChartsData
      ?.filter((item: any) => item.step !== "Not Ready")
      ?.reduce((acc: any[], item: any) => {
        const stepName = capitalizeEachWords(item.step);

        // Check if this step already exists
        const existingIndex = acc.findIndex(
          (entry) => entry.label === stepName,
        );

        if (existingIndex !== -1) {
          // If exists, add to the count
          acc[existingIndex].count += item.count_brands;
          acc[existingIndex].value += item.count_brands;
          // Recalculate percentage if needed, or handle it later
        } else {
          // If doesn't exist, add new entry
          acc.push({
            label: stepName,
            count: item.count_brands,
            percent: item.percentage ? `${item.percentage}%` : "0%",
            value: item.count_brands,
            color: COLORS[stepName] || "#FF9800", // Use normalized stepName for color lookup
          });
        }

        return acc;
      }, []) || [];

  // Calculate total from the aggregated chartData
  const total = chartData.reduce(
    (sum: number, item: any) => sum + item.count,
    0,
  );

  // Recalculate percentages based on the new total
  chartData.forEach((item: any) => {
    item.percent =
      total > 0 ? `${((item.count / total) * 100).toFixed(2)}%` : "0%";
  });

  // const chartData = [
  //   {
  //     label: "Properties",
  //     count: loadedCount,
  //     percent: `${Math.round(loadedPerc)}%`,
  //     value: loadedCount,
  //     color: COLORS[0],
  //   },
  //   {
  //     label: "Unifier",
  //     count: total - loadedCount,
  //     percent: `${100 - Math.round(loadedPerc)}%`,
  //     value: total - loadedCount,
  //     color: COLORS[1],
  //   },
  //   {
  //     label: "Alt. Identifier",
  //     count: total - loadedCount,
  //     percent: `${100 - Math.round(loadedPerc)}%`,
  //     value: total - loadedCount,
  //     color: COLORS[2],
  //   },
  //   {
  //     label: "Fetcher",
  //     count: total - loadedCount,
  //     percent: `${100 - Math.round(loadedPerc)}%`,
  //     value: total - loadedCount,
  //     color: COLORS[3],
  //   },
  //   {
  //     label: "Advisor",
  //     count: total - loadedCount,
  //     percent: `${100 - Math.round(loadedPerc)}%`,
  //     value: total - loadedCount,
  //     color: COLORS[4],
  //   },
  // ];

  // Handle case where all data set empty
  if (chartData.length === 0 || total === 0) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="center">
      <PieChart
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        series={[
          {
            data: chartData.map((d: any) => ({
              label: d.label,
              value: d.value,
              color: d.color,
            })),
            innerRadius: 40,
            outerRadius: 50,
          } as any,
        ]}
        {...size}
        slotProps={{ legend: { hidden: true } }}
      >
        <PieCenterLabel total={total} />
      </PieChart>

      <CustomLegend data={chartData} />
    </Stack>
  );
}
